import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { apiCall } from '../utils/api';

function Giveaway({ user, onLogout }) {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Create form fields
  const [name, setName] = useState('');
  const [baseDomain, setBaseDomain] = useState('');
  const [codePrefix, setCodePrefix] = useState('');
  const [utmSource, setUtmSource] = useState('qr_scan');
  const [utmMedium, setUtmMedium] = useState('print');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    // Check if user is admin
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/projects');
      return;
    }
    
    fetchCampaigns();
  }, [user, navigate]);

  const fetchCampaigns = async () => {
    try {
      const response = await apiCall('/api/giveaway/campaigns');
      const data = await response.json();
      setCampaigns(data.campaigns);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('base_domain', baseDomain);
      formData.append('code_prefix', codePrefix);
      formData.append('utm_source', utmSource);
      formData.append('utm_medium', utmMedium);
      formData.append('utm_campaign', utmCampaign);

      const response = await apiCall('/api/giveaway/campaigns', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        toast.success('Campaign created!');
        setShowCreateForm(false);
        setName('');
        setBaseDomain('');
        setCodePrefix('');
        setUtmCampaign('');
        fetchCampaigns();
      } else {
        toast.error(`Failed: Error ${response.status}`);
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setCreating(false);
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Delete this campaign and all its codes?')) return;

    try {
      const response = await apiCall(`/api/giveaway/campaigns/${campaignId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Campaign deleted');
        fetchCampaigns();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar onLogout={onLogout} user={user} />
      
      <div className="main-content">
        <Topbar user={user} pageTitle="Giveaway Campaign Manager" />
        
        <div className="page-content">
          <div className="page-header d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4>🎁 Giveaway Campaigns</h4>
              <p className="text-muted">Manage bulk QR codes for promotional campaigns</p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateForm(!showCreateForm)}
            >
              {showCreateForm ? '✕ Cancel' : '+ New Campaign'}
            </button>
          </div>

          {/* Inline Create Form */}
          {showCreateForm && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">Create New Giveaway Campaign</h5>
              </div>
              
              <form onSubmit={createCampaign}>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="label">Campaign Name *</label>
                    <input
                      type="text"
                      className="input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      placeholder="Holiday Tax Giveaway 2025"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="label">Base Domain *</label>
                    <input
                      type="text"
                      className="input"
                      value={baseDomain}
                      onChange={(e) => setBaseDomain(e.target.value)}
                      required
                      placeholder="letstalktaxes.io/community"
                    />
                    <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                      Where QR codes will redirect (without https://)
                    </p>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="label">Code Prefix (optional)</label>
                    <input
                      type="text"
                      className="input"
                      value={codePrefix}
                      onChange={(e) => setCodePrefix(e.target.value)}
                      placeholder="GB12- or GIFT- or WIN-"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="label">UTM Source</label>
                    <input
                      type="text"
                      className="input"
                      value={utmSource}
                      onChange={(e) => setUtmSource(e.target.value)}
                      placeholder="qr_scan"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="label">UTM Medium</label>
                    <input
                      type="text"
                      className="input"
                      value={utmMedium}
                      onChange={(e) => setUtmMedium(e.target.value)}
                      placeholder="print"
                    />
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={creating}
                  >
                    {creating ? 'Creating...' : 'Create Campaign'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Campaign List */}
          {campaigns.length === 0 ? (
            <div className="card empty-state">
              <div className="empty-state-icon">🎁</div>
              <h3>No giveaway campaigns yet</h3>
              <p className="text-muted">Create your first campaign to manage bulk QR codes</p>
              <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
                Create Campaign
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2">
              {campaigns.map((campaign) => (
                <div key={campaign.id} className="card">
                  <div className="card-header">
                    <h5 className="card-title mb-0">{campaign.name}</h5>
                  </div>
                  
                  <p className="text-muted mb-2" style={{ fontSize: '0.875rem' }}>
                    🌐 {campaign.base_domain}
                  </p>
                  
                  <div className="row mb-3">
                    <div className="col-4">
                      <div style={{ textAlign: 'center', padding: '0.75rem', background: 'var(--gray-100)', borderRadius: 'var(--border-radius)' }}>
                        <h5 style={{ color: 'var(--primary)', marginBottom: '0.25rem' }}>{campaign.total_codes || 0}</h5>
                        <p className="text-muted mb-0" style={{ fontSize: '0.7rem' }}>Codes</p>
                      </div>
                    </div>
                    <div className="col-4">
                      <div style={{ textAlign: 'center', padding: '0.75rem', background: 'var(--gray-100)', borderRadius: 'var(--border-radius)' }}>
                        <h5 style={{ color: 'var(--success)', marginBottom: '0.25rem' }}>{campaign.active_codes || 0}</h5>
                        <p className="text-muted mb-0" style={{ fontSize: '0.7rem' }}>Active</p>
                      </div>
                    </div>
                    <div className="col-4">
                      <div style={{ textAlign: 'center', padding: '0.75rem', background: 'var(--gray-100)', borderRadius: 'var(--border-radius)' }}>
                        <h5 style={{ color: 'var(--info)', marginBottom: '0.25rem' }}>{campaign.total_scans || 0}</h5>
                        <p className="text-muted mb-0" style={{ fontSize: '0.7rem' }}>Scans</p>
                      </div>
                    </div>
                  </div>

                  {campaign.code_prefix && (
                    <p className="mb-2" style={{ fontSize: '0.75rem' }}>
                      <span className="badge badge-info">Prefix: {campaign.code_prefix}</span>
                    </p>
                  )}

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-primary"
                      style={{ flex: 1 }}
                      onClick={() => navigate(`/giveaway/${campaign.campaign_id}`)}
                    >
                      Manage Codes
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => deleteCampaign(campaign.campaign_id)}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Giveaway;
